import React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { buttonVariants, Card, CardContent, Separator } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { HandCard } from "iconoir-react";

import { useServices } from "@src/context/ServicesProvider";
import { useWallet } from "@src/context/WalletProvider";
import { useManagedEscrowFaqModal } from "@src/hooks/useManagedEscrowFaqModal";
import type { WalletBalance } from "@src/hooks/useWalletBalance";
import { LinkTo } from "../../shared/LinkTo";
import { AddFundsLink } from "../../user/AddFundsLink";

interface ManagedWalletPopupProps extends React.PropsWithChildren {
  walletBalance?: WalletBalance | null;
  dependencies?: typeof DEPENDENCIES;
}

export const DEPENDENCIES = {
  Card,
  CardContent,
  Separator,
  LinkTo,
  AddFundsLink,
  HandCard,
  FormattedNumber,
  useWallet,
  useManagedEscrowFaqModal,
  useServices
};

export const ManagedWalletPopup: React.FC<ManagedWalletPopupProps> = ({ walletBalance, dependencies: d = DEPENDENCIES }) => {
  const { isTrialing } = d.useWallet();
  const { showManagedEscrowFaqModal } = d.useManagedEscrowFaqModal();
  const { urlService } = d.useServices();

  return (
    <div className="w-[300px] p-2">
      {isTrialing && (
        <div className="mb-2 text-sm font-bold">
          <p className="text-center">
            <FormattedMessage id="wallet.managedwalletpopup.freetrial" defaultMessage="Free Trial" />
          </p>
        </div>
      )}
      <d.Card>
        {walletBalance ? (
          <d.CardContent className="space-y-2 p-4">
            <div className="flex items-center justify-between space-x-2">
              <span className="text-xs">
                <FormattedMessage id="wallet.managedwalletpopup.creditsremaining" defaultMessage="Credits Remaining:" />
              </span>
              <span>
                <d.FormattedNumber
                  value={walletBalance.totalDeploymentGrantsUSD}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="USD"
                />
              </span>
            </div>

            <d.Separator />

            <div className="flex items-center justify-between space-x-2">
              <span className="text-xs">
                <FormattedMessage id="wallet.managedwalletpopup.deposits" defaultMessage="Deposits:" />
              </span>
              <span>
                <d.FormattedNumber
                  value={walletBalance.totalDeploymentEscrowUSD}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="USD"
                />
              </span>
            </div>
          </d.CardContent>
        ) : (
          <d.CardContent className="p-4 text-xs">
            <FormattedMessage id="wallet.managedwalletpopup.unknownbalance" defaultMessage="Wallet Balance is unknown because the blockchain is unavailable" />
          </d.CardContent>
        )}
      </d.Card>
      <div className="mb-2 mt-1 flex items-center justify-end">
        <d.LinkTo className="text-xs text-foreground no-underline" onClick={() => showManagedEscrowFaqModal()}>
          <FormattedMessage id="wallet.managedwalletpopup.whatsthis" defaultMessage="What's this?" />
        </d.LinkTo>
      </div>

      {isTrialing && (
        <div className="my-2 text-center text-xs text-muted-foreground">
          <FormattedMessage
            id="wallet.managedwalletpopup.trialnotice"
            defaultMessage="Once your Free credits run out, deployments will automatically close. To continue, create an account and add funds with your credit card. Deployments from your Free Trial get transferred when creating a new account."
          />
        </div>
      )}

      <div className="flex flex-col items-center justify-end space-y-2 pt-2">
        <d.AddFundsLink
          className={cn("w-full space-x-2 hover:no-underline", buttonVariants({ variant: "default" }))}
          href={urlService.billing({ openPayment: true })}
        >
          <HandCard className="text-xs" />
          <span className="whitespace-nowrap">
            <FormattedMessage id="wallet.managedwalletpopup.addfunds" defaultMessage="Add Funds" />
          </span>
        </d.AddFundsLink>
      </div>
    </div>
  );
};
